const decodeSerializedHtml = (html: string) => {
  let decodedHtml = String(html ?? "").trim();

  // Some settings APIs return the editor value as a JSON-encoded string.
  // Decode at most twice to handle a double-serialized value without looping.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (!(decodedHtml.startsWith('"') && decodedHtml.endsWith('"'))) {
      break;
    }

    try {
      const parsed = JSON.parse(decodedHtml);
      if (typeof parsed !== "string") {
        break;
      }
      decodedHtml = parsed.trim();
    } catch (_error) {
      break;
    }
  }

  // Recover malformed serialized values that have escaped attribute quotes
  // but only one of the surrounding JSON quotes.
  if (/\\["']/.test(decodedHtml) && /<\/?[a-z]/i.test(decodedHtml)) {
    decodedHtml = decodedHtml
      .replace(/^(["'])\s*(?=<)/, "")
      .replace(/(?<=>)\s*(["'])$/, "");
  }

  // Also support escaped markup received without the surrounding JSON quotes.
  decodedHtml = decodedHtml
    .replace(/\\(["'])/g, "$1")
    .replace(/\\\//g, "/")
    .replace(/\\r\\n|\\n|\\r/g, "\n");

  // Convert encoded tags to markup. Ordinary entities are left for the browser
  // so text such as "A &amp; B" retains the correct representation.
  if (/&(?:amp;)?lt;\/?[a-z][\s\S]*?(?:&(?:amp;)?gt;)/i.test(decodedHtml)) {
    const textarea = document.createElement("textarea");
    for (let attempt = 0; attempt < 2; attempt += 1) {
      textarea.innerHTML = decodedHtml;
      const decodedEntities = textarea.value;
      if (decodedEntities === decodedHtml) {
        break;
      }
      decodedHtml = decodedEntities;
    }
  }

  return decodedHtml;
};

export const formatHtmlContent = (html: string) => {
  const decodedHtml = decodeSerializedHtml(html);
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(decodedHtml, "text/html");
  const phonePattern = /(\+?\d[\d\s()-]{7,})/g;
  const textNodes: Text[] = [];
  const walker = documentNode.createTreeWalker(
    documentNode.body,
    NodeFilter.SHOW_TEXT
  );

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const parentTag = textNode.parentElement?.tagName;
    if (parentTag !== "SCRIPT" && parentTag !== "STYLE") {
      textNodes.push(textNode);
    }
  }

  textNodes.forEach((textNode) => {
    // Restore the space after a sentence when the editor dropped it while
    // merging paragraphs ("Portal.By using" -> "Portal. By using"). Only a
    // lowercase letter or digit, a full stop, then a capital letter qualifies,
    // so domains, decimals and abbreviations such as "U.S." are untouched.
    const original = textNode.nodeValue || "";
    const spaced = original
      .replace(/([a-z0-9])\.([A-Z])/g, "$1. $2")
      // Editors leave "&nbsp; " (non-breaking + ordinary space) after a
      // marker, which renders as a visible double space.
      .replace(/\u00a0(?= )|(?<= )\u00a0/g, "");
    if (spaced !== original) {
      textNode.nodeValue = spaced;
    }

    const text = textNode.nodeValue || "";
    phonePattern.lastIndex = 0;
    if (!phonePattern.test(text)) {
      return;
    }

    phonePattern.lastIndex = 0;
    const fragment = documentNode.createDocumentFragment();
    let lastIndex = 0;

    text.replace(phonePattern, (match, _phone, offset: number) => {
      fragment.append(
        documentNode.createTextNode(text.slice(lastIndex, offset))
      );
      const span = documentNode.createElement("span");
      span.style.whiteSpace = "nowrap";
      span.textContent = match;
      fragment.append(span);
      lastIndex = offset + match.length;
      return match;
    });

    fragment.append(documentNode.createTextNode(text.slice(lastIndex)));
    textNode.replaceWith(fragment);
  });

  // Justified text in the portal's narrow columns stretches the word spacing
  // badly and the designs are left-aligned throughout, so drop that one
  // alignment. Explicit centre/right alignment is left as authored.
  documentNode.body
    .querySelectorAll<HTMLElement>("[style*='text-align']")
    .forEach((element) => {
      if (element.style.textAlign === "justify") {
        element.style.removeProperty("text-align");
        if (!element.getAttribute("style")?.trim()) {
          element.removeAttribute("style");
        }
      }
    });

  // Admin-authored links to external pages (terms of service, cookie policy…)
  // open in a new tab so the customer does not lose the login / payment flow.
  documentNode.body.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") || "";
    if (/^https?:\/\//i.test(href)) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  });

  return documentNode.body.innerHTML;
};

// The admin editor saves "<strong>Heading</strong><br>text" wrapped in a <p>.
// Lift that leading bold line out of the paragraph so it renders as the
// heading, the same as the unwrapped default template.
export const liftLeadingHeading = (html: string) => {
  const documentNode = new DOMParser().parseFromString(html, "text/html");
  const paragraph = documentNode.body.firstElementChild;
  if (paragraph?.tagName !== "P") return html;

  const significant = (node: ChildNode | null) => {
    let current = node;
    while (
      current &&
      current.nodeType === Node.TEXT_NODE &&
      !current.textContent?.trim()
    ) {
      current = current.nextSibling;
    }
    return current;
  };

  const heading = significant(paragraph.firstChild);
  if (!heading || !["STRONG", "B"].includes((heading as Element).tagName)) {
    return html;
  }
  const lineBreak = significant(heading.nextSibling);
  if ((lineBreak as Element | null)?.tagName !== "BR") return html;

  lineBreak!.remove();
  paragraph.parentNode?.insertBefore(heading, paragraph);
  const rest = significant(paragraph.firstChild);
  if (!rest) paragraph.remove();
  return documentNode.body.innerHTML;
};
